import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import TableSortLabel from "@mui/material/TableSortLabel";
import ButtonUI from "../button/buttons";
import {
  Box,
  Checkbox,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  OutlinedInput,
  TextField,
  Menu,
  Button,
} from "@mui/material";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import SimCardDownloadIcon from "@mui/icons-material/SimCardDownload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useState, useEffect } from "react";
import { InputDatePicker } from "../inputsDatePicker/inputDatePicker";
import SearchIcon from "@mui/icons-material/Search";
import { Search } from "@mui/icons-material";
import "./tableMaterial.css";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import BasicSelect from "../inputs/selectInput";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Chip from "@mui/material/Chip";
import { CSVLink } from "react-csv";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface Column {
  id: string;
  label: string;
  align?: "right" | "left" | "center";
  render?: (value: any) => JSX.Element; // Permite pasar JSX en las celdas
}

interface TableProps {
  columns: Column[];
  rows: any[];
  loading?: boolean;
  isDate?: boolean;
  id?: string;
  valueInput?: string;
  onChangeInput?: any;
  report?: boolean;
  iconButton?: any;
  rowsPerPage?: any;
  setRowsPerPage?: any;
  page?: any;
  setPage?: any;
  totalRows?: any;
  startDate?: Date | null;
  endDate?: Date | null;
  setStartDate?: any;
  setEndDate?: any;
  onClickSearch?: any;
  isSearch?: boolean;
  onClickFilters?: any;
  setStatus?: any;
  status?: any;
  isClean?: any;
  hideFooterPagination?: boolean
}

export default function TableMaterial({
  columns,
  loading,
  id,
  report,
  rows,
  rowsPerPage,
  setRowsPerPage,
  page,
  setPage,
  totalRows,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  isDate,
  valueInput,
  onChangeInput,
  onClickSearch,
  isSearch = true,
  onClickFilters,
  setStatus,
  status,
  isClean = false,
  hideFooterPagination = false
}: TableProps) {
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string>("created_at");
  const [isLargeScreen, setIsLargeScreen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
  const openMenu = Boolean(anchorEl);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1280);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Volver a la primera página cuando cambie el número de filas por página
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClickMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const prepareDataForExport = () => {
    return rows.map((row) => {
      const cleanRow: any = {};
      columns.forEach((column) => {
        if (
          column.id === "info" ||
          column.label.toLowerCase().includes("información") ||
          column.label.toLowerCase().includes("informacion")
        ) {
          return;
        }

        const value = row[column.id];

        if (value && typeof value === "object" && value.props) {
          if (value.props.type !== undefined) {
            cleanRow[column.label] = value.props.type;
          }
          else if (value.props.value !== undefined) {
            cleanRow[column.label] = value.props.value;
          }
          else if (typeof value.props.children === "string") {
            cleanRow[column.label] = value.props.children;
          }
          else if (typeof value.props.children === "number") {
            cleanRow[column.label] = value.props.children;
          }
          else {
            cleanRow[column.label] = "";
          }
        } else {
          cleanRow[column.label] = value;
        }
      });
      return cleanRow;
    });
  };

  const exportToExcel = () => {
    setIsGeneratingReport(true);
    try {
      const dataToExport = prepareDataForExport();
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Reporte");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

      saveAs(blob, "reporte.xlsx");
    } finally {
      setIsGeneratingReport(false);
      handleCloseMenu();
    }
  };

  const exportToPDF = async () => {
    setIsGeneratingReport(true);
    const tableElement = document.getElementById(id || "table-container");
    if (!tableElement) {
      setIsGeneratingReport(false);
      return;
    }

    const clonedTable = tableElement.cloneNode(true) as HTMLElement;

    clonedTable.style.position = "absolute";
    clonedTable.style.left = "-9999px";
    clonedTable.style.top = "0";
    clonedTable.style.width = "max-content";
    clonedTable.style.maxWidth = "none";
    clonedTable.style.overflow = "visible";
    clonedTable.style.zIndex = "-1";

    document.body.appendChild(clonedTable);

    try {
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(clonedTable, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      document.body.removeChild(clonedTable);

      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error("El canvas generado está vacío");
      }

      const imgData = canvas.toDataURL("image/jpeg", 1.0);

      if (!imgData || imgData.length < 100) {
        throw new Error("La imagen generada está vacía");
      }

      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? "landscape" : "portrait",
        unit: "mm",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;

      pdf.addImage(
        imgData,
        "JPEG",
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio
      );
      pdf.save("reporte.pdf");
    } catch (error) {
      if (document.body.contains(clonedTable)) {
        document.body.removeChild(clonedTable);
      }
      alert("Error al generar PDF. Por favor, intenta de nuevo.");
    } finally {
      setIsGeneratingReport(false);
      handleCloseMenu();
    }
  };

  const prepareCSVData = () => {
    return prepareDataForExport();
  };

  const handleChange = (event: SelectChangeEvent) => {
    setStatus(event.target.value as string);
  };

  const statusSelect = [
    { status: "Estatus", value: "ALL" },
    { status: "Aprobada", value: "APPROVED" },
    { status: "Declinada", value: "DECLINED" },
    { status: "Pendiente", value: "CHARGE_PENDING" },
  ];

  return (
    <div className="table-material-container">
      <Paper className="table-material-paper">
        <Container maxWidth="xl" className="filters-container">
          <div className="filters-grid">
            {report && (
              <div className="export-button-container">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SimCardDownloadIcon />}
                  endIcon={<ArrowDropDownIcon />}
                  onClick={handleClickMenu}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    padding: "8px 16px",
                  }}
                >
                  Generar Reporte
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={openMenu}
                  onClose={handleCloseMenu}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <MenuItem disabled={isGeneratingReport}>
                    <CSVLink
                      data={prepareCSVData()}
                      filename="reporte.csv"
                      style={{
                        textDecoration: "none",
                        color: isGeneratingReport ? "#ccc" : "inherit",
                        width: "100%",
                        pointerEvents: isGeneratingReport ? "none" : "auto",
                      }}
                      onClick={() => {
                        setIsGeneratingReport(true);
                        setTimeout(() => {
                          setIsGeneratingReport(false);
                          handleCloseMenu();
                        }, 500);
                      }}
                    >
                      Descargar CSV
                    </CSVLink>
                  </MenuItem>
                  <MenuItem onClick={exportToExcel} disabled={isGeneratingReport}>
                    Descargar Excel
                  </MenuItem>
                  <MenuItem onClick={exportToPDF} disabled={isGeneratingReport}>
                    Descargar PDF
                  </MenuItem>
                </Menu>
              </div>
            )}

            {isSearch && (
              <div className="search-input-container">
                <TextField
                  size="small"
                  id="outlined-basic"
                  label="Búsqueda por email"
                  variant="outlined"
                  value={valueInput}
                  onChange={(e: any) => onChangeInput(e)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton aria-label="search" onClick={onClickSearch}>
                          <Search />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            )}

            {status && (
              <div className="search-input-container">
                <Select
                  size="small"
                  variant="outlined"
                  id="outlined-basic"
                  value={status || "ALL"} // <- asegúrate de que sea string vacío al limpiar                  label="Estatus"
                  fullWidth
                  defaultValue={"ALL"}
                  onChange={handleChange}
                >
                  {statusSelect.map((it: any) => (
                    <MenuItem value={it.value} key={it.value}>
                      <div
                        className={`status-select ${it.value.toLowerCase()}`}
                      >
                        {it.status}
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </div>
            )}

            {isDate && (
              <div className="date-picker-container">
                <InputDatePicker
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                />
              </div>
            )}
            {isClean && (
              <div>
                <ButtonUI
                  icon={<CleaningServicesIcon />}
                  title="Limpiar filtros"
                  onClick={onClickFilters}
                />
              </div>
            )}
          </div>
        </Container>

        <TableContainer id={id} className="table-container">
          {loading ? (
            <div className="loading-container">
              <CircularProgress />
            </div>
          ) : (
            <>
              <Table
                className={
                  isLargeScreen ? "responsive-table-large" : "responsive-table"
                }
                aria-label="reusable table"
              >
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align || "left"}
                        sortDirection={orderBy === column.id ? order : false}
                        className={`${isLargeScreen ? "table-cell-large" : "table-cell"
                          } table-header-cell`}
                      >
                        <TableSortLabel
                          disabled
                          // active={orderBy === column.id}
                          // direction={orderBy === column.id ? order : "asc"}
                          // onClick={() => handleSort(column.id)}

                          active={false} // nunca marcar como activo
                          direction="asc" // no importa porque no se usará
                          onClick={undefined}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.align || "left"}
                          className={
                            isLargeScreen ? "table-cell-large" : "table-cell"
                          }
                        >
                          {column.render
                            ? column.render(row[column.id])
                            : row[column.id]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {
                !hideFooterPagination && (
                  <div className="pagination-container">
                    <TablePagination
                      component="div"
                      count={totalRows || 0}
                      page={page || 0}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      className="pagination-toolbar"
                      labelRowsPerPage="Filas por página:"
                    />
                  </div>
                )
              }

            </>
          )}
        </TableContainer>
      </Paper>
    </div>
  );
}
